import React, { useCallback, useEffect, useState, memo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../scss/components/_ag-table.scss";
import { getServerSideDatasourceSplitted } from "../helpers/serverside-datasource/splitted-table";
import "ag-grid-charts-enterprise";
import { useCookiesContext } from "../contexts/CookiesContext";
import { searchPlaceholder } from "@revbox/localization";
import { useDebouncedCallback } from "../hooks/useDebounceCallback";

const containerStyle = { width: "100%", height: "100%" };
const gridStyle = { minHeight: "200px", width: "100%" };
const defaultColDef = {
  flex: 1,
  width: 200,
};

export const AgSplittedTable = memo(
  ({
    classifiedColumns,
    currentPeriod,
    previousPeriod,
    getData,
    setCountByStatus,
    customColumns,
    getContextMenuItems,
    skin,
    filter,
    inventoryId,
    colWidths,
    onRowClick,
    status = null,
    getRowStyle,
    onCellMouseOver,
  }) => {
    const [search, setSearch] = useState("");
    const [gridApi, setGridApi] = useState(null);
    const [columnDefs, setColumnDefs] = useState([]);
    const [sortingParams, setSortingParams] = useState({
      columnName: null,
      sortOrder: null,
    });
    const { getLanguageAbbr } = useCookiesContext();
    const languageAbbr = getLanguageAbbr();
    const search_placeholder = searchPlaceholder[languageAbbr];

    const onFilterTextBoxChanged = useDebouncedCallback((e) => {
      setSearch(e.target.value);
    }, 500);

    const onGridReady = useCallback(
      (params) => {
        setGridApi(params.api);
        const datasource = getServerSideDatasourceSplitted(
          getData,
          currentPeriod,
          previousPeriod,
          colWidths,
          classifiedColumns,
          setColumnDefs,
          setCountByStatus,
          status,
          search,
          customColumns,
          inventoryId,
          sortingParams,
          filter
        );
  
        params.api.setGridOption("serverSideDatasource", datasource);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [currentPeriod, previousPeriod, colWidths, classifiedColumns, status, search, customColumns, inventoryId, sortingParams, filter],
    )
    

    const onSortChanged = useCallback((params) => {
      const columnState = params.api.getColumnState();

      const sorted = columnState.find(col => col.sort);

      if (sorted) {
        setSortingParams({
          columnName: sorted.colId,
          sortOrder: sorted.sort,
        });
      } else {
        setSortingParams(null);
      }
    }, []);

    useEffect(() => {
      if (gridApi) {
        const datasource = getServerSideDatasourceSplitted(
          getData,
          currentPeriod,
          previousPeriod,
          colWidths,
          classifiedColumns,
          setColumnDefs,
          setCountByStatus,
          status,
          search,
          customColumns,
          inventoryId,
          sortingParams,
          filter
        );

        gridApi.setGridOption("serverSideDatasource", datasource);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, currentPeriod, previousPeriod, sortingParams, gridApi, status, filter]);

    return (
      <div style={containerStyle}>
        <div
          style={gridStyle}
          className={
            skin === "dark"
              ? "ag-theme-quartz-dark ag-theme-quartz"
              : "ag-theme-quartz-light ag-theme-quartz"
          }
        >
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              className="input-search"
              placeholder={search_placeholder}
              onInput={onFilterTextBoxChanged}
            />
          </div>
          <AgGridReact
            columnDefs={columnDefs}
            onRowClicked={onRowClick}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onSortChanged={onSortChanged}
            pagination={true}
            onCellMouseOver={onCellMouseOver}
            getRowStyle={getRowStyle}
            paginationPageSize={10}
            paginationPageSizeSelector={false}
            cacheBlockSize={10}
            rowModelType={"serverSide"}
            domLayout={"autoHeight"}
            allowContextMenuWithControlKey={true}
            getContextMenuItems={getContextMenuItems}
          />
        </div>
      </div>
    );
  }
)
